import { useState } from 'react';
import { ChevronDown, ChevronUp, CircleCheck, CircleHelp, CircleX, Pen } from 'lucide-react';
import { Dropdown } from 'react-bootstrap';
import { VerificationCriteriaParams, VerificationCriteriaStatus } from '@/backend/access/verificationCriteria/types';
import { useRequirementsStore } from '@/frontend/store/RequirementsStore';
import { CircleXFilled } from '@/frontend/SVGs/CircleXFilled';
import { CircleHelpFilled } from '@/frontend/SVGs/CircleHelpFilled';
import { I18n } from '@/i18n/I18n';
import { CircleCheckFilled } from '@/frontend/SVGs/CircleCheckFilled';

interface VerificationCriteriaCardProps {
  criteria: VerificationCriteriaParams;
}

const STATUS_BADGE_CONFIG: Record<VerificationCriteriaStatus, { label: string; className: string; icon: React.ReactNode } | null> = {
  [VerificationCriteriaStatus.PENDING]: null,
  [VerificationCriteriaStatus.ACCEPTED]: {
    label: 'accepted',
    className: 'vc-badge-accepted',
    icon: <CircleCheckFilled color="var(--green-700)" />,
  },
  [VerificationCriteriaStatus.REJECTED]: {
    label: 'rejected',
    className: 'vc-badge-rejected',
    icon: <CircleXFilled color="var(--red-700)" />,
  },
  [VerificationCriteriaStatus.MISSING_EVIDENCE]: {
    label: 'missingEvidence',
    className: 'vc-badge-missing',
    icon: <CircleHelpFilled />,
  },
};

export function VerificationCriteriaCard({ criteria }: VerificationCriteriaCardProps) {
  const updateVerificationCriteria = useRequirementsStore((s) => s.updateVerificationCriteria);
  const [expanded, setExpanded] = useState(criteria.status === VerificationCriteriaStatus.MISSING_EVIDENCE);

  const handleStatusChange = (newStatus: VerificationCriteriaStatus) => {
    void updateVerificationCriteria({ ...criteria, status: newStatus });
  };

  if (criteria.status === VerificationCriteriaStatus.PENDING) {
    return (
      <div className="vc-card">
        <div className="d-flex justify-content-between">
          <div>
            <p className="vc-card-title">{criteria.title}</p>
            {criteria.description && (
              <div className="vc-card-description">{criteria.description}</div>
            )}
          </div>
          <div className='d-flex flex-column gap-2'>
            <button
              className="fusa-btn-icon"
              aria-label={I18n.get('accepted')}
              onClick={() => handleStatusChange(VerificationCriteriaStatus.ACCEPTED)}
            >
              <CircleCheck color="var(--green-400)" size={16} />
            </button>
            <button
              className="fusa-btn-icon"
              aria-label={I18n.get('rejected')}
              onClick={() => handleStatusChange(VerificationCriteriaStatus.REJECTED)}
            >
              <CircleX color="var(--red-500)" size={16} />
            </button>
            <button
              className="fusa-btn-icon"
              aria-label={I18n.get('missingEvidence')}
              onClick={() => handleStatusChange(VerificationCriteriaStatus.MISSING_EVIDENCE)}
            >
              <CircleHelp color="var(--neutral-400)" size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  const badgeConfig = STATUS_BADGE_CONFIG[criteria.status];
  const menuOptions = [
    { status: VerificationCriteriaStatus.ACCEPTED, labelKey: 'changeToAccepted', icon: <CircleCheck color="var(--green-400)" size={16} /> },
    { status: VerificationCriteriaStatus.REJECTED, labelKey: 'changeToRejected', icon: <CircleX color="var(--red-500)" size={16} /> },
    { status: VerificationCriteriaStatus.MISSING_EVIDENCE, labelKey: 'changeToMissingEvidence', icon: <CircleHelp color="var(--neutral-400)" size={16} /> },
  ].filter(option => option.status !== criteria.status);

  return (
    <div className="vc-card">
      <div className="d-flex justify-content-between">
        <p className="vc-card-title">{criteria.title}</p>
        <Dropdown>
          <Dropdown.Toggle as="button" className="fusa-btn-icon" aria-label="edit" bsPrefix="vc-edit-toggle">
            <Pen size={16} color="var(--neutral-400)" />
          </Dropdown.Toggle>
          <Dropdown.Menu className="vc-status-menu">
            {menuOptions.map((opt) => (
              <Dropdown.Item
                key={opt.status}
                className="vc-status-menu-item"
                onClick={() => handleStatusChange(opt.status)}
              >
                {opt.icon}
                {I18n.get(opt.labelKey)}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>
      {expanded && criteria.description && (
        <div className="vc-card-description">{criteria.description}</div>
      )}
      <div className="d-flex justify-content-between">
        {badgeConfig && (
          <div className={`vc-badge ${badgeConfig.className}`}>
            {badgeConfig.icon}
            <span className="vc-badge-label">{I18n.get(badgeConfig.label)}</span>
          </div>
        )}
        <button
          className="fusa-btn-icon"
          aria-label={expanded ? 'collapse' : 'expand'}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? <ChevronUp size={16} color="var(--neutral-400)" /> : <ChevronDown size={16} color="var(--neutral-400)" />}
        </button>
      </div>
    </div>
  );
}
